import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { getT } from '@/i18n';
import { ApiError, api, apiConfigured, type Profile } from '@/lib/api';
import { useAuth } from './AuthProvider';

/**
 * A GRUNDO-profil (`users/{uid}`) betöltése.
 *
 * Külön a Firebase Auth-tól: egy Firebase-fiók létezhet GRUNDO-profil nélkül
 * is (pl. ha a regisztráció félbeszakadt, vagy a fiók még a backend előtt
 * jött létre). Ilyenkor a felhasználót a felhasználónév-választóra visszük.
 */

export type ProfileStatus =
  | 'idle' // nincs bejelentkezve
  | 'loading'
  | 'ready'
  | 'missing' // van Firebase-fiók, de nincs GRUNDO-profil
  | 'unavailable' // a backend nincs beállítva (VITE_API_BASE_URL)
  | 'error';

export interface ProfileApi {
  status: ProfileStatus;
  profile: Profile | null;
  error: string;
  reload: () => Promise<void>;
  /**
   * CSENDES frissítés: a profil adata cserélődik, a `status` NEM.
   *
   * ⚠️ A `reload` a globális állapotot `loading`-ra állítja, amitől az
   * egész app a Splash-re vált és a NYITOTT KÉPERNYŐ LESZERELŐDIK. Ez
   * teljes betöltéskor helyes, háttérbeli utánatöltésnél viszont
   * katasztrofális: a képernyő újra felépül, a „már frissítettem"
   * jelölője nulázódik, és megint frissít — végtelen villogás (Geri
   * jelezte a Jelvények oldalon, 2026-09-17).
   *
   * Hiba esetén CSENDBEN marad a régi profil: egy elmaradt frissítés
   * jobb, mint egy hibára váltó app.
   */
  refresh: () => Promise<void>;
  patchProfile: (patch: Partial<Profile>) => void;
  createProfile: (username: string, dob: string) => Promise<void>;
  /**
   * Igaz, amíg a fiók EBBEN A MUNKAMENETBEN jött létre — az „Első követések"
   * onboarding-lépés (`docs/02` → Onboarding 7.) ebből tudja, hogy friss
   * regisztrációról van szó, nem egy már hetek óta aktív felhasználóról.
   * Szándékosan NEM perzisztens (nincs Firestore-mező mögötte): egy meglévő
   * élő felhasználót ez a képernyő sosem lephet meg később, csak azt, aki
   * MOST, ebben a folyamatban regisztrált.
   */
  justCreated: boolean;
  clearJustCreated: () => void;
}

const ProfileContext = createContext<ProfileApi | null>(null);

export function useProfile(): ProfileApi {
  const value = useContext(ProfileContext);
  if (!value) throw new Error('useProfile csak a ProfileProvider alatt hívható');
  return value;
}

export function ProfileProvider({ children }: { children: ReactNode }) {
  const { status: authStatus, user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [status, setStatus] = useState<ProfileStatus>('idle');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (authStatus !== 'signed-in') {
      setProfile(null);
      setStatus('idle');
      return;
    }
    if (!apiConfigured) {
      // A backend még nincs beállítva — az app nem áll meg emiatt.
      setStatus('unavailable');
      return;
    }

    setStatus('loading');
    setError('');
    let timeout: number | undefined;
    try {
      /**
       * Egy elvesző native/CORS kérés nem maradhat örökké a Splash mögött.
       * A backend hibáját a már létező ProfileError képernyő mutatja meg.
       */
      const { profile: next } = await Promise.race([
        api.me(),
        new Promise<never>((_resolve, reject) => {
          timeout = window.setTimeout(
            () => reject(new Error(getT().profileError.timeout)),
            12_000,
          );
        }),
      ]);
      setProfile(next);
      setStatus('ready');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'profile_missing') {
        setProfile(null);
        setStatus('missing');
        return;
      }
      setError(err instanceof Error ? err.message : getT().profileError.loadFailed);
      setStatus('error');
    } finally {
      if (timeout !== undefined) window.clearTimeout(timeout);
    }
  }, [authStatus]);

  useEffect(() => {
    void load();
  }, [load, user?.uid]);

  const refresh = useCallback(async () => {
    if (authStatus !== 'signed-in' || !apiConfigured) return;
    try {
      const { profile: next } = await api.me();
      setProfile(next);
    } catch {
      /* Marad a korábbi profil — lásd a `refresh` leírását. */
    }
  }, [authStatus]);

  const [justCreated, setJustCreated] = useState(false);

  const createProfile = useCallback(async (username: string, dob: string) => {
    const { profile: next } = await api.register(username, dob);
    setProfile(next);
    setStatus('ready');
    setJustCreated(true);
  }, []);

  const clearJustCreated = useCallback(() => setJustCreated(false), []);

  const patchProfile = useCallback((patch: Partial<Profile>) => {
    setProfile((current) => (current === null ? null : { ...current, ...patch }));
  }, []);

  return (
    <ProfileContext.Provider
      value={{
        status,
        profile,
        error,
        reload: load,
        refresh,
        patchProfile,
        createProfile,
        justCreated,
        clearJustCreated,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
}
